import React from "react";
import {
  AppBar, Toolbar, Typography, IconButton, Box, Avatar,
  Menu, MenuItem, Tooltip, Divider, Badge, Popover,
  List, ListItem, ListItemText, ListItemAvatar, Button,
  Chip
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import {
  Assignment as AssignmentIcon, Quiz as QuizIcon,
  Grade as GradeIcon, Forum as ForumIcon,
  Campaign as AnnouncementIcon, School as SchoolIcon,
  Circle as DotIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../../store/authStore";
import NotificationService from "../../services/notifications.service";
import LanguageSwitcher from "../ui/LanguageSwitcher";
import { institutionLabel, nameFontSize } from "../../utils/institutionName";
import { formatDistanceToNow } from "date-fns";

const TYPE_CONFIG = {
  assignment:   { icon: <AssignmentIcon fontSize="small" />, color: "primary.main" },
  quiz:         { icon: <QuizIcon fontSize="small" />, color: "secondary.main" },
  grade:        { icon: <GradeIcon fontSize="small" />, color: "success.main" },
  discussion:   { icon: <ForumIcon fontSize="small" />, color: "info.main" },
  announcement: { icon: <AnnouncementIcon fontSize="small" />, color: "warning.main" },
  enrollment:   { icon: <SchoolIcon fontSize="small" />, color: "primary.main" },
  system:       { icon: <SettingsIcon fontSize="small" />, color: "text.secondary" },
};

const getTypeConfig = (type) => TYPE_CONFIG[type] || { icon: <NotificationsNoneIcon fontSize="small" />, color: "primary.main" };

// ── Notification Bell + Popover ───────────────────────────────────────────────
const NotificationBell = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("nav");
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const { data: countData } = useQuery({
    queryKey: ["notifications-unread-count"],
    queryFn: async () => {
      const res = await NotificationService.getUnreadCount();
      return res?.data?.count ?? 0;
    },
    refetchInterval: 60_000, // poll every minute
    staleTime: 30_000
  });

  const { data: previewData } = useQuery({
    queryKey: ["notifications-preview"],
    queryFn: async () => {
      const res = await NotificationService.getNotifications({ limit: 6, isArchived: false });
      return res?.data?.notifications || [];
    },
    enabled: Boolean(anchorEl),
    staleTime: 15_000
  });

  const { mutate: markAllRead } = useMutation({
    mutationFn: () => NotificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-unread-count"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-preview"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    }
  });

  const { mutate: markRead } = useMutation({
    mutationFn: (id) => NotificationService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-unread-count"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-preview"] });
    }
  });

  const unreadCount = countData ?? 0;
  const preview = previewData || [];

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = (notif) => {
    if (!notif.isRead) markRead(notif._id);
    if (notif.data?.link) {
      navigate(notif.data.link);
    }
    handleClose();
  };

  return (
    <>
      <Tooltip title={t("notifications")}>
        <IconButton color="inherit" onClick={handleOpen} size="small" sx={{ mr: 0.5 }}>
          <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={99}>
            {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 380, maxHeight: 520, display: "flex", flexDirection: "column" } }}
      >
        {/* Popover header */}
        <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: 1, borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>{t("notifications")}</Typography>
            {unreadCount > 0 && (
              <Chip label={unreadCount} size="small" color="error" sx={{ height: 18, fontSize: "0.65rem" }} />
            )}
          </Box>
          {unreadCount > 0 && (
            <Tooltip title={t("markAllRead")}>
              <IconButton size="small" onClick={() => markAllRead()}>
                <DoneAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Preview list */}
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {preview.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <NotificationsNoneIcon sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
              <Typography variant="body2" color="text.secondary">{t("notificationsEmpty")}</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {preview.map((n, i) => {
                const cfg = getTypeConfig(n.type);
                return (
                  <React.Fragment key={n._id}>
                    <ListItem
                      button
                      onClick={() => handleNotificationClick(n)}
                      sx={{
                        px: 2, py: 1.25,
                        bgcolor: n.isRead ? "transparent" : "action.hover",
                        alignItems: "flex-start"
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 40, mt: 0.25 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: "transparent", color: cfg.color }}>
                          {cfg.icon}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {!n.isRead && (
                              <DotIcon sx={{ fontSize: 8, color: "primary.main", flexShrink: 0 }} />
                            )}
                            <Typography variant="body2" fontWeight={n.isRead ? 400 : 700} noWrap>
                              {n.title}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                              {n.message.length > 80 ? n.message.slice(0, 78) + "…" : n.message}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {n.createdAt ? formatDistanceToNow(new Date(n.createdAt), { addSuffix: true }) : ""}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {i < preview.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ borderTop: 1, borderColor: "divider", p: 1 }}>
          <Button
            fullWidth
            size="small"
            onClick={() => { navigate("/notifications"); handleClose(); }}
          >
            {t("seeAll")}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

// ── Navbar ────────────────────────────────────────────────────────────────────
const Navbar = ({ onToggleSidebar }) => {
  const navigate = useNavigate();
  const { t } = useTranslation("nav");
  const user = useAuthStore((state) => state.user);
  const university = useAuthStore((state) => state.university);
  const logout = useAuthStore((state) => state.logout);

  /**
   * Show the institution for tenant roles; the platform brand for super_admin.
   *
   * The app bar is CHROME — it says which tenant you are in, it is not the
   * institutional statement the login portal makes — and it is one fixed-height
   * line beside a language toggle, a bell and an avatar. A 75-character name
   * ("Université Nationale des Sciences, Technologies, Ingénierie et
   * Mathématiques") had only bad options there: it ellipsised to "Université
   * Nationale des Scien…", which names no institution in particular, and on a
   * phone it truncated before the distinguishing word.
   *
   * So take the longest form that fits: the full name when it is short enough,
   * otherwise the abbreviation the institution chose for ITSELF — never a
   * machine-made one — with the complete name in the tooltip either way.
   */
  const { label: brandName, full: brandFull } =
    user && user.role !== "super_admin" && university?.name
      ? institutionLabel(university)
      : { label: "Vitrine LMS", full: "Vitrine LMS" };
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <AppBar
      position="sticky"
      elevation={1}
      sx={{
        backgroundColor: "background.paper",
        color: "text.primary",
        borderBottom: "1px solid",
        borderColor: "divider"
      }}
    >
      <Toolbar>
        <IconButton edge="start" color="inherit" onClick={onToggleSidebar} sx={{ mr: 2 }}
          aria-label={t("tooltips.expandMenu", { ns: "common" })}
          title={t("tooltips.expandMenu", { ns: "common" })}>
          <MenuIcon />
        </IconButton>

        <Typography
          variant="h6"
          noWrap
          sx={{
            flexGrow: 1,
            minWidth: 0,
            mr: 1,
            fontWeight: 600,
            cursor: "pointer",
            // Even the chosen abbreviation can be long, and a tenant may have
            // none at all — so still scale to what is actually being rendered.
            // Size only: this line keeps its own `noWrap` + ellipsis, because
            // the bar is a fixed height.
            fontSize: nameFontSize(brandName, { xs: [1, 0.82], sm: [1.25, 0.95] }),
            overflow: "hidden",
            textOverflow: "ellipsis",
            transition: "color 0.2s ease",
            "&:hover": { color: "primary.main" },
          }}
          onClick={() => navigate("/dashboard")}
          title={brandFull}
        >
          {brandName}
        </Typography>

        {/* Language toggle (available to everyone, incl. before sign-in) */}
        <LanguageSwitcher />

        {/* Notification bell */}
        {user && <NotificationBell />}

        {/* User menu */}
        <Tooltip title={t("account")}>
          <IconButton onClick={handleMenuOpen} size="small">
            <Avatar sx={{ width: 32, height: 32 }} src={user?.avatar}>
              {user?.firstName?.charAt(0) || <AccountCircleIcon />}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={() => { navigate("/profile"); handleMenuClose(); }}>
            <AccountCircleIcon sx={{ mr: 1 }} /> {t("profile")}
          </MenuItem>
          <MenuItem onClick={() => { navigate("/settings"); handleMenuClose(); }}>
            <SettingsIcon sx={{ mr: 1 }} /> {t("settings")}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <LogoutIcon sx={{ mr: 1 }} /> {t("logout")}
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
